import { Button, Card, CardBody, CardHeader, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, Spinner } from "@nextui-org/react";
import { PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import useCustomer from "../../../../hooks/master-data/useCustomer";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import ControlledReactSelect from "../../../../components/elements/ControlledReactSelect";
import ControlledInput from "../../../../components/elements/ControlledInput";
import ModalAlert from "../../../../components/elements/ModalAlert";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import DataPICSelector from "../../../../components/fragments/admin/report/ModalDataPIC";

export default function IndexPage() {
    const { 
        isEdit,
        selectedCompany,
        setSelectedCompany,
        onClickEdit, 
        onClickDelete,
        onSubmitDelete,
        onCloseForm,
        onSubmitForm, 
        modalForm: { onOpenModalForm, isOpenModalForm, onOpenChangeModalForm },
        modalAlert: { isOpenModalAlert, onOpenChangeModalAlert },
        hookForm: { control, setValue, register, handleSubmit, formState: { errors, isSubmitting, dirtyFields, } },
        dataFetch: {
            data,
            isLoading,
            dataCompany,
            isLoadingCompany,
            dataPIC,
            isLoadingDataPIC,
            mutatePIC,
            province,
            regency,
            district,
            village
        }, 
    } = useCustomer();

    const columns = useMemo(() =>  [
        { field: 'namaCustomer', headerName: 'Nama Perusahaan Customer' },
        { field: 'telepon', headerName: 'Telepon',},
        { field: 'fax', headerName: 'Fax', },
        { field: 'email', headerName: 'Email', },
        { field: 'alamat', headerName: 'Alamat', },
        {
            field: 'action',
            headerName: 'Aksi',
            renderCell: (params) => (
                <div className="flex items-center gap-1">
                    <Button size='sm' onPress={() => onClickEdit(params.row)} color='warning' isIconOnly><PencilSquareIcon className='size-4'/></Button>
                    <Button size='sm' onPress={() => onClickDelete(params.row.id)} color='danger' isIconOnly><TrashIcon className='size-4'/></Button>
                </div>
            ),
            sortable: false,
            filterable: false
        },
    ], [onClickEdit, onClickDelete]);

    return(
        <RootAdmin>
            <Card className="w-full mt-3" radius="sm">
                <CardHeader>
                    <p className="text-md">Daftar Perusahaan Customer</p>
                </CardHeader>
                <Divider />
                <CardBody className='w-full h-[550px] p-5'>
                    <div className="flex justify-between mb-5">
                        <div className="flex flex-col justify-end">
                            <Button onPress={onOpenModalForm} size="sm" color="primary" startContent={<PlusIcon className="size-4 stroke-2"/>}>Tambah</Button>
                        </div>
                        <div className="w-1/4">
                            <FilterReactSelect
                                defaultValue={selectedCompany}
                                options={dataCompany?.data?.map(item => ({label: item.name, value: item.id}))}
                                setValue={setSelectedCompany}
                                placeholder="Pilih Perusahaan"
                                isLoading={isLoadingCompany}
                            />
                        </div>
                    </div>
                    <ClientDataGrid
                        columns={columns}
                        rows={data}
                        isLoading={isLoading}
                    />
                </CardBody>
            </Card>

            <ModalAlert isOpen={isOpenModalAlert} onOpenChange={onOpenChangeModalAlert} onSubmit={onSubmitDelete} icon="danger"/>
            <Modal scrollBehavior="inside" size="3xl" isOpen={isOpenModalForm} onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader>{isEdit ? 'Ubah' : 'Tambah'} Perusahaan Customer</ModalHeader>
                            <ModalBody>
                                <form onSubmit={handleSubmit(onSubmitForm)}>
                                    {
                                        isEdit && !village 
                                        ?
                                        <div className="flex items-center justify-center">
                                            <Spinner/>
                                        </div>
                                        :
                                        <>
                                            <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-6'>  
                                                <ControlledReactSelect
                                                    control={control}
                                                    label="Perusahaan"
                                                    name="companyId"
                                                    options={dataCompany?.data?.map((item) => ({value: item.id, label: item.name}))}
                                                    isLoading={isLoadingCompany}
                                                />
                                                <ControlledInput label="Customer" name="namaCustomer" isRequired={true} control={control} />
                                                <ControlledInput label="Alamat" name="alamat" isRequired={true} control={control} />
                                                <ControlledInput label="Email" name="email" isRequired={true} control={control} />
                                                <ControlledInput label="Telepon" name="telepon" isRequired={true} control={control} />
                                                <ControlledInput label="Fax" name="fax" isRequired={true} control={control} />
                                                <ControlledInput label="Latitude" name="latitude" isRequired={true} control={control} />
                                                <ControlledInput label="Longitude" name="longitude" isRequired={true} control={control} />  
                                                <Select
                                                    {...register('provinceId')}
                                                    isRequired
                                                    variant="faded"
                                                    label="Provinsi"
                                                    labelPlacement="outside"
                                                    placeholder='Pilih...'
                                                    onChange={(e) => setValue('provinceId', e.target.value)}
                                                    color={errors.provinceId ? 'danger' : 'default'}
                                                    isInvalid={errors.provinceId}
                                                    errorMessage={errors.provinceId && errors.provinceId.message}
                                                >
                                                    {province?.map(item => (
                                                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                                                    ))}
                                                </Select>                    
                                                <Select
                                                    {...register('regencyId')}
                                                    isRequired
                                                    variant="faded"
                                                    label="Kabupaten"
                                                    labelPlacement="outside"
                                                    placeholder='Pilih...'
                                                    onChange={(e) => setValue('regencyId', e.target.value)}
                                                    color={errors.regencyId ? 'danger' : 'default'}
                                                    isInvalid={errors.regencyId}
                                                    errorMessage={errors.regencyId && errors.regencyId.message}
                                                >
                                                    {regency?.map(item => (
                                                        <SelectItem key={item.id}>{item.name}</SelectItem>
                                                    ))}
                                                </Select>                    
                                                <Select
                                                    {...register('districtId')}
                                                    isRequired
                                                    variant="faded"
                                                    label="Kecamatan"
                                                    labelPlacement="outside"
                                                    placeholder='Pilih...'
                                                    onChange={(e) => setValue('districtId', e.target.value)}
                                                    color={errors.districtId ? 'danger' : 'default'}
                                                    isInvalid={errors.districtId}
                                                    errorMessage={errors.districtId && errors.districtId.message}
                                                >
                                                    {district?.map(item => (
                                                        <SelectItem key={item.id}>{item.name}</SelectItem>
                                                    ))}
                                                </Select>                    
                                                <Select
                                                    {...register('villageId')}
                                                    isRequired
                                                    variant="faded"
                                                    label="Kelurahan"
                                                    labelPlacement="outside"
                                                    placeholder='Pilih...'
                                                    color={errors.villageId ? 'danger' : 'default'}
                                                    isInvalid={errors.villageId}
                                                    errorMessage={errors.villageId && errors.villageId.message}
                                                >
                                                    {village?.map(item => (
                                                        <SelectItem key={item.id}>{item.name}</SelectItem>
                                                    ))}
                                                </Select>  
                                                <div className='col-span-2'>
                                                    <div className='flex gap-3'>
                                                        <div className="flex-1">
                                                            <ControlledReactSelect 
                                                                control={control} 
                                                                label="Data PIC" 
                                                                name="dataPICIds" 
                                                                isLoading={isLoadingDataPIC} 
                                                                options={dataPIC?.map(item => ({ value: item.id, label: item.namaPIC }))} 
                                                                isMulti={true}
                                                            />
                                                        </div>
                                                        <DataPICSelector 
                                                            type={'customer'} 
                                                            mutatePIC={mutatePIC}
                                                        />
                                                    </div>
                                                </div>               
                                            </div>
                                            <div className='flex items-center gap-1'>
                                                <Button isLoading={isSubmitting} isDisabled={isSubmitting || (isEdit && Object.keys(dirtyFields).length === 0)} type='submit' color='primary'>{isEdit ? 'Simpan' : 'Tambah'}</Button>
                                                <Button isDisabled={isSubmitting} color='danger' variant='faded' onPress={onClose}>Batal</Button>
                                            </div>
                                        </>
                                    }
                                </form>
                            </ModalBody>
                            <ModalFooter>

                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </RootAdmin>
    )
}